import {RoomState} from '../../../../../shared/RoomState'
import {LobbyImage} from '@colonist/types'
import {ObjectUtils} from '@colonist/utils'
import {Sound} from '../../../../shared/scripts/Sound'
import SoundType = Sound.SoundType
import {UIMRoomExitConfirmationViewPopup} from './UIMRoomExitConfirmationViewPopup'
import {UIMRoomPageController} from './UIMRoomPageController'
import {UIMRoomEditPageController} from './UIMRoomEditPageController'
import {UIMRoomSetupPageController} from './UIMRoomSetupPageController'
import {SocketLobbySend} from '../../../../shared/scripts/socket/SocketLobbySend'
import {RoomType} from '@colonist/lobby-types'
import {UIUrlController} from '../../../../shared/scripts/ui_html/UIUrlController'
import {socket} from '../../../../shared/scripts/EntryShared'
import {UIOnlineFriendController} from '../../../../shared/scripts/ui_friends/UIOnlineFriendController'
import {UIRoomSettingsController} from '../../../../shared/scripts/ui_room/UIRoomSettingsController'

export enum RoomStep {
    RoomPage,
    EditRoom,
    SetupRoom
}

type UIMRoomStepControllers = UIMRoomSetupPageController | UIMRoomEditPageController | UIMRoomPageController

export class UIMCreateRoomController {
    private roomState: RoomState

    private roomSetupController: UIMRoomSetupPageController | undefined
    private roomEditPageController: UIMRoomEditPageController | undefined
    private roomPageController: UIMRoomPageController | undefined

    private pageOnNextUpdate: RoomStep | undefined = undefined

    init() {
        socket.controller.registerSessionEstablishedObserver(() => {
            if(this.isUserOnCreateRoomFlow()) {
                this.handleSocketReconnection()
            }
        })
    }

    updateRoomState(roomState: RoomState) {
        UIMCreateRoomController.playUpdateSounds(this.roomState, roomState)
        this.roomState = roomState

        this.updateAllControllersWithRoomState()
        this.initNextStepForRoomCreationFlow()
        this.showPageOnNextUpdate()
    }

    private updateAllControllersWithRoomState() {
        this.roomSetupController?.update(this.roomState)
        this.roomEditPageController?.update(this.roomState)
        this.roomPageController?.update(this.roomState)
    }

    private initNextStepForRoomCreationFlow() {
        const userOnCreateRoomFlow = this.pageOnNextUpdate == undefined
        if(userOnCreateRoomFlow) this.pageOnNextUpdate = RoomStep.RoomPage
    }

    private showPageOnNextUpdate(activeTabIndex?: number) {
        window.history.replaceState(null, '', '#' + this.roomState.gameSetting.id)

        switch(this.pageOnNextUpdate) {
            case RoomStep.SetupRoom: return this.showSetupScreen(activeTabIndex)
            case RoomStep.EditRoom: return this.showEditRoomScreen(activeTabIndex)
            case RoomStep.RoomPage: return this.showActiveRoomScreen(activeTabIndex)
            default: return
        }
    }

    private showActiveRoomScreen(tabIndex?: number) {
        if(this.roomPageController == undefined) this.roomPageController = new UIMRoomPageController(this.roomState)
        UIOnlineFriendController.userEnteredRoom()
        this.showRoomStep(this.roomPageController)
        this.roomPageController.showView(tabIndex)
    }

    private showEditRoomScreen(tabIndex?: number) {
        if(this.roomEditPageController == undefined) this.roomEditPageController = new UIMRoomEditPageController(this.roomState)
        this.showRoomStep(this.roomEditPageController)
        this.roomEditPageController.showView(tabIndex)
    }

    private showSetupScreen(tabIndex?: number) {
        if(this.roomSetupController == undefined) this.roomSetupController = new UIMRoomSetupPageController(this.roomState)
        this.showRoomStep(this.roomSetupController)
        this.roomSetupController.showView(tabIndex)
    }

    receivedChatMessage(avatar: LobbyImage, message: string, username?: string) {
        this.roomPageController?.receivedChatMessage(avatar, message, username)
    }

    setShowPageOnNextUpdate(page: RoomStep, forceUpdate: boolean = false, activeTabIndex?: number) {
        this.pageOnNextUpdate = page
        if(forceUpdate) this.showPageOnNextUpdate(activeTabIndex)
    }

    initCreateRoomView() {
        this.pageOnNextUpdate = RoomStep.SetupRoom
        SocketLobbySend.clickedCreateRoom(RoomType.Standard)
        setTimeout(() => {
            void SocketLobbySend.clickedRoomSettingChangePrivateGame(true)
            UIRoomSettingsController.initRoomSettingFromLocalStorage()
        }, 500)
    }

    onExitRoom(callback?: () => void, forceExit?: boolean) {
        if(this.pageOnNextUpdate == RoomStep.EditRoom && !forceExit) {
            this.pageOnNextUpdate = RoomStep.RoomPage
            this.roomPageController?.showView()
            return this.roomEditPageController?.exitView()
        }

        new UIMRoomExitConfirmationViewPopup(() => {
            this.leaveRoom()
            this.roomSetupController?.roomSettingsController.setPrivateRoomStateSync(false)
            UIOnlineFriendController.userOnlineAndIdle()
            if(callback) callback()
        })
    }

    showRoomStep(roomController: UIMRoomStepControllers) {
        if(this.roomPageController != roomController) this.roomPageController?.hideView()
        if(this.roomSetupController != roomController) this.roomSetupController?.hideView()
        if(this.roomEditPageController != roomController) this.roomEditPageController?.hideView()
    }

    private clearRoom() {
        this.roomPageController?.hideView()
        this.roomSetupController?.exitView()
        this.roomEditPageController?.exitView()
        this.pageOnNextUpdate = undefined
    }

    leaveRoom() {
        window.location.hash = ''
        this.clearRoom()
        SocketLobbySend.clickedRoomExit()
    }

    private static playUpdateSounds(oldRoomState: RoomState, newRoomState: RoomState) {
        if(oldRoomState == undefined) return
        const settingsChanged = !ObjectUtils.isObjectEqual(oldRoomState.gameSetting, newRoomState.gameSetting)
        if(!settingsChanged) return
        Sound.playSound(SoundType.RoomSettingUpdated)
    }

    isUserOnCreateRoomFlow() {
        return this.pageOnNextUpdate != undefined
    }

    // eslint-disable-next-line class-methods-use-this
    disableChatInput() {
        const chatInput = document.querySelector('input.m-room-players-chat-input') as HTMLInputElement | undefined
        if(chatInput) chatInput.disabled = true
    }

    isCreateRoomButNotAHost() {
        if(!this.isUserOnCreateRoomFlow()) return false
        return !this.roomState.isHost(socket.controller.session.id)
    }

    private handleSocketReconnection() {
        this.clearRoom()
        UIUrlController.accessGameFromLink(async(roomId) => {
            await SocketLobbySend.accessGameFromLink(roomId)
        })
    }
}
